
import React, { useState, useEffect, useMemo } from 'react';
import { UserRole, RequestStatus, ProfessionalProfile, ChatRequest, User, SchedulingStatus, StudentProfile } from '../types.ts';
import { dbService } from '../services/dbService.ts';

interface ProfessionalDashboardProps {
  user: any;
  onStartMeeting: (req: ChatRequest) => void;
  onAcceptAndChat: (reqId: string) => void;
  onChangeTab?: (tab: string) => void;
}

const ProfessionalDashboard: React.FC<ProfessionalDashboardProps> = ({ 
  user, 
  onStartMeeting,
  onAcceptAndChat,
  onChangeTab,
}) => {
  const [profile] = useState<ProfessionalProfile | undefined>(dbService.getProfessionalProfile(user.id));
  const [requests, setRequests] = useState<ChatRequest[]>([]);
  const [rejectingRequest, setRejectingRequest] = useState<ChatRequest | null>(null);
  const [viewingStudentDetail, setViewingStudentDetail] = useState<StudentProfile | null>(null);
  const [subTab, setSubTab] = useState<'radar' | 'pipeline'>('pipeline');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchInitial = async () => {
      setIsLoading(true);
      await new Promise(r => setTimeout(r, 1500));
      setRequests(dbService.getRequestsForUser(user.id, UserRole.PROFESSIONAL));
      setIsLoading(false);
    };
    fetchInitial();

    const interval = setInterval(() => {
      setRequests(dbService.getRequestsForUser(user.id, UserRole.PROFESSIONAL));
    }, 5000);
    return () => clearInterval(interval);
  }, [user.id]);

  const handleAccept = (reqId: string) => {
    const req = requests.find(r => r.id === reqId);
    if (req) {
      const updated = { ...req, status: RequestStatus.ACCEPTED, updatedAt: Date.now() };
      dbService.saveRequest(updated);
      dbService.addMessageToRequest(reqId, { 
        senderId: user.id, 
        text: `Protocol accepted. Looking forward to our synchronization cycle.`
      });
      onAcceptAndChat(reqId);
    }
  };

  const handleDecline = (reqId: string, reason: string) => {
    const req = requests.find(r => r.id === reqId);
    if (req) {
      const updated = { ...req, status: RequestStatus.DECLINED, declineReason: reason, updatedAt: Date.now() };
      dbService.saveRequest(updated);
      setRequests(dbService.getRequestsForUser(user.id, UserRole.PROFESSIONAL));
      setRejectingRequest(null);
    }
  };

  const pending = useMemo(() => requests.filter(r => r.status === RequestStatus.PENDING), [requests]);
  const active = useMemo(() => requests.filter(r => r.status === RequestStatus.ACCEPTED || r.status === RequestStatus.COMPLETED), [requests]);

  // For dossier logic
  const activeReq = useMemo(() => {
    if (!viewingStudentDetail) return null;
    return requests.find(r => r.studentId === viewingStudentDetail.userId && r.status === RequestStatus.PENDING);
  }, [viewingStudentDetail, requests]);

  return (
    <div className="p-4 md:p-12 lg:p-16 bg-[#F8FAFC] dark:bg-slate-950 min-h-screen transition-colors duration-500 font-sans relative overflow-x-hidden">
        <header className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-8 md:mb-12 gap-6 relative z-10">
          <div>
            <span className="text-[9px] font-black uppercase tracking-[0.4em] text-indigo-600 dark:text-indigo-400 mb-2 block">Management Console</span>
            <h1 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">Console.</h1>
          </div>
          
          <div className="flex bg-white dark:bg-slate-900 p-1 rounded-2xl md:rounded-[1.8rem] border border-slate-200 dark:border-slate-800 shadow-xl w-full md:w-auto">
             <button 
               onClick={() => setSubTab('radar')}
               className={`flex-1 md:flex-none px-6 md:px-10 py-3 md:py-4 rounded-xl md:rounded-[1.4rem] text-[10px] md:text-[11px] font-black uppercase tracking-widest transition-all ${subTab === 'radar' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-900'}`}
             >
               Radar
             </button>
             <button 
               onClick={() => setSubTab('pipeline')}
               className={`flex-1 md:flex-none px-6 md:px-10 py-3 md:py-4 rounded-xl md:rounded-[1.4rem] text-[10px] md:text-[11px] font-black uppercase tracking-widest transition-all ${subTab === 'pipeline' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-900'}`}
             >
               Pipeline
             </button>
          </div>
        </header>

        {subTab === 'radar' && (
           <div className="hidden md:block relative h-[600px] w-full bg-white dark:bg-slate-900/40 rounded-[4rem] border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden animate-in fade-in">
              {isLoading ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-50">
                  <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-xs font-black uppercase tracking-[0.3em] text-indigo-600 animate-pulse">Searching for Requests...</p>
                </div>
              ) : (
                <div className="relative w-full h-full flex items-center justify-center">
                   <div className="z-20 w-32 h-32 bg-slate-900 dark:bg-indigo-600 rounded-[2.5rem] flex flex-col items-center justify-center text-white shadow-4xl border-8 border-white dark:border-slate-900">
                      <p className="text-[8px] font-black uppercase tracking-widest opacity-60 mb-1">PRO</p>
                      <span className="text-3xl font-black">{user.fullName[0]}</span>
                   </div>
                   {pending.map((req, i) => {
                      const radius = 300 - ((req.synergyScore || 80) * 2);
                      const angle = (i * (360 / Math.max(pending.length, 1))) * (Math.PI / 180);
                      const x = Math.cos(angle) * radius;
                      const y = Math.sin(angle) * radius;
                      return (
                         <div key={req.id} className="absolute" style={{ transform: `translate(calc(50% + ${x}px), calc(50% + ${y}px))` }}>
                            <button onClick={() => { const sp = dbService.getStudentProfile(req.studentId); if (sp) setViewingStudentDetail(sp); }} className="w-16 h-16 bg-white dark:bg-slate-800 border-4 border-slate-200 dark:border-slate-700 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                               <span className="font-black text-slate-900 dark:text-white">{dbService.getUsers().find(u => u.id === req.studentId)?.fullName[0]}</span>
                            </button>
                         </div>
                      );
                   })}
                </div>
              )}
           </div>
        )}

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 animate-in slide-in-from-right-10 ${subTab === 'radar' ? 'md:hidden' : ''}`}>
           {isLoading ? (
              <div className="col-span-1 lg:col-span-2 h-96 flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-[3rem] border border-slate-200 dark:border-slate-800">
                 <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                 <p className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-400 animate-pulse">Searching for Inbound Requests...</p>
              </div>
           ) : (
             <>
               <section className="space-y-6 md:space-y-8">
                  <h3 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                     <div className="w-1.5 h-6 md:w-2 md:h-8 bg-amber-400 rounded-full"></div> Inbound Sync
                  </h3>
                  <div className="space-y-4 md:space-y-6">
                     {pending.length === 0 && <div className="p-12 md:p-16 bg-white dark:bg-slate-900 rounded-[2.5rem] md:rounded-[3rem] border border-dashed border-slate-200 dark:border-slate-800 text-center text-slate-400 font-bold text-xs uppercase">No active protocols.</div>}
                     {pending.map(req => {
                       const sp = dbService.getStudentProfile(req.studentId);
                       const meetsGPA = !profile?.targetStudentGPA || profile.targetStudentGPA === 'any' || (parseFloat(sp?.gpa || "0") >= parseFloat(profile.targetStudentGPA));
                       return (
                         <div key={req.id} className="bg-white dark:bg-slate-900 p-6 md:p-10 rounded-[2.5rem] md:rounded-[3.5rem] border border-slate-200 dark:border-slate-800 shadow-xl group transition-all relative overflow-hidden">
                            <div className="flex flex-col md:flex-row justify-between items-start gap-4 md:gap-6 mb-6 md:mb-8">
                               <div className="flex gap-4 md:gap-6 items-center" onClick={() => setViewingStudentDetail(sp!)}>
                                  <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-900 dark:bg-indigo-600 text-white rounded-xl md:rounded-2xl flex items-center justify-center font-black text-xl md:text-2xl shadow-lg">{sp?.fullName[0]}</div>
                                  <div className="min-w-0">
                                     <h4 className="font-black text-slate-900 dark:text-white text-lg md:text-2xl tracking-tight truncate leading-none mb-1">{sp?.fullName}</h4>
                                     <p className="text-[9px] md:text-[11px] font-black text-slate-400 uppercase tracking-widest truncate">{sp?.school} • {sp?.major}</p>
                                  </div>
                                </div>
                               <div className="flex gap-2 w-full md:w-auto">
                                  <button onClick={() => setRejectingRequest(req)} className="flex-1 md:flex-none px-4 py-3 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-red-50 hover:text-red-500">Decline</button>
                                  <button onClick={() => handleAccept(req.id)} className="flex-[2] md:flex-none px-6 py-3 bg-slate-900 dark:bg-indigo-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg hover:scale-105 transition-transform">Accept</button>
                               </div>
                            </div>

                            <div className="bg-blue-50 dark:bg-indigo-900/10 p-5 md:p-8 rounded-[2rem] md:rounded-[2.5rem] border border-blue-100 dark:border-indigo-900/30 mb-6">
                               <p className="text-xs md:text-sm font-bold text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                  {req.synergyThesis || req.matchRationale}
                               </p>
                               <div className="grid grid-cols-2 gap-2">
                                  <div className={`flex items-center gap-2 p-2 rounded-lg border ${meetsGPA ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-50 text-amber-600 border-amber-100'}`}>
                                     <i className={`fas ${meetsGPA ? 'fa-check' : 'fa-info'} text-[8px]`}></i>
                                     <span className="text-[8px] font-black uppercase tracking-widest">GPA: {sp?.gpa || 'N/A'}</span>
                                  </div>
                                  <div className="flex items-center gap-2 p-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700">
                                     <i className="fas fa-tag text-[8px] text-slate-400"></i>
                                     <span className="text-[8px] font-black uppercase tracking-widest truncate">{sp?.major}</span>
                                  </div>
                               </div>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-[1.5rem] border border-slate-100 dark:border-slate-800">
                              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed italic">"{req.studentNote}"</p>
                            </div>
                         </div>
                       );
                     })}
                  </div>
               </section>

               <section className="space-y-6 md:space-y-8">
                  <h3 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                     <div className="w-1.5 h-6 md:w-2 md:h-8 bg-emerald-500 rounded-full"></div> Active Cycles
                  </h3>
                  <div className="space-y-3">
                    {active.length === 0 && <div className="p-12 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-dashed border-slate-200 dark:border-slate-800 text-center text-slate-400 font-bold text-xs uppercase">Queue empty.</div>}
                    {active.map(req => {
                      const student = dbService.getUsers().find(u => u.id === req.studentId);
                      return (
                        <div key={req.id} onClick={() => onAcceptAndChat(req.id)} className="bg-white dark:bg-slate-900 p-4 md:p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-lg flex items-center justify-between active:scale-95 transition-transform hover:shadow-xl">
                          <div className="flex items-center gap-4">
                             <div className="relative shrink-0">
                                <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-xl flex items-center justify-center font-black text-lg">{student?.fullName[0]}</div>
                                {req.schedulingStatus === SchedulingStatus.CONFIRMED && <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white"></div>}
                             </div>
                             <div className="min-w-0">
                                <p className="font-black text-slate-900 dark:text-white text-sm truncate">{student?.fullName}</p>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{req.schedulingStatus === SchedulingStatus.CONFIRMED ? 'Active Hub' : 'Awaiting times'}</p>
                             </div>
                          </div>
                          <div className="flex gap-2">
                            {req.schedulingStatus === SchedulingStatus.CONFIRMED && <button onClick={(e) => { e.stopPropagation(); onStartMeeting(req); }} className="w-10 h-10 bg-emerald-500 text-white rounded-xl flex items-center justify-center"><i className="fas fa-video text-xs"></i></button>}
                            <i className="fas fa-chevron-right text-slate-200 self-center"></i>
                          </div>
                        </div>
                      );
                    })}
                  </div>
               </section>
             </>
           )}
        </div>

        {/* Student Detail Dossier - Immersive Mobile Sheet */}
        {viewingStudentDetail && (
           <div className="fixed inset-0 z-[1001] bg-slate-900/50 dark:bg-black/95 backdrop-blur-xl flex items-end md:items-start justify-center p-0 md:p-6 overflow-y-auto animate-in fade-in duration-500">
             <div className="bg-white dark:bg-slate-950 w-full max-w-4xl rounded-t-[3rem] md:rounded-[5rem] shadow-4xl p-8 md:p-16 relative flex flex-col gap-8 animate-in slide-in-from-bottom-10 duration-500 max-h-[95vh] md:max-h-none overflow-y-auto scrollbar-hide md:my-10 border-t border-slate-100 dark:border-slate-800">
                <button onClick={() => setViewingStudentDetail(null)} className="sticky top-0 ml-auto md:absolute md:top-12 md:right-12 w-12 h-12 bg-slate-50 dark:bg-slate-900 text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-full flex items-center justify-center transition-all z-50"><i className="fas fa-times"></i></button>
                
                <header className="flex flex-col md:flex-row gap-6 md:gap-10 items-center md:items-start text-center md:text-left">
                   <div className="w-24 h-24 md:w-32 md:h-32 bg-slate-900 dark:bg-indigo-600 text-white rounded-[2rem] md:rounded-[2.5rem] flex items-center justify-center text-4xl font-black shadow-2xl shrink-0">{viewingStudentDetail.fullName.charAt(0)}</div>
                   <div className="flex-1">
                      <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-3">
                         <span className="text-[8px] font-black text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1 rounded-full uppercase tracking-widest border border-indigo-100 dark:border-indigo-900/50">Verified Student</span>
                         <span className="text-[8px] font-black text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-1 rounded-full uppercase tracking-widest border border-emerald-100 dark:border-emerald-900/50">GPA: {viewingStudentDetail.gpa || 'N/A'}</span>
                      </div>
                      <h3 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tighter leading-none mb-3">{viewingStudentDetail.fullName}</h3>
                      <p className="text-xl md:text-2xl font-bold text-slate-400 tracking-tight">{viewingStudentDetail.major} <span className="text-slate-300 mx-1">/</span> {viewingStudentDetail.school}</p>
                   </div>
                </header>

                {/* SAGE MATCH INTELLIGENCE SECTION */}
                {activeReq && (activeReq.synergyThesis || activeReq.matchRationale) && (
                  <section className="bg-indigo-50 dark:bg-indigo-900/20 p-8 rounded-[2.5rem] border border-indigo-100 dark:border-indigo-900/30 animate-in slide-in-from-top-4">
                     <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-indigo-600 text-white rounded-lg flex items-center justify-center shadow-lg"><i className="fas fa-brain text-xs"></i></div>
                        <h4 className="text-[10px] font-black text-indigo-900 dark:text-indigo-300 uppercase tracking-widest">Sage Match Intelligence</h4>
                     </div>
                     <p className="text-lg font-bold text-slate-700 dark:text-slate-200 leading-relaxed">
                        "{activeReq.synergyThesis || activeReq.matchRationale}"
                     </p>
                  </section>
                )}

                <section className="bg-slate-900 dark:bg-blue-950 text-white p-8 md:p-12 rounded-[2.5rem] md:rounded-[4rem] shadow-2xl relative overflow-hidden group">
                   <div className="relative z-10">
                      <h4 className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-400 mb-6">Identity Summary</h4>
                      <p className="text-xl md:text-2xl font-black leading-tight tracking-tight mb-8">
                        {viewingStudentDetail.bio || "Student identity protocol pending narrative synchronization."}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-white/10">
                        <div>
                           <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Primary Aspiration</p>
                           <p className="text-sm font-bold leading-relaxed">"{viewingStudentDetail.goals}"</p>
                        </div>
                        <div>
                           <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Interests</p>
                           <div className="flex flex-wrap gap-2">
                             {viewingStudentDetail.interests?.map((tag, i) => (
                               <span key={i} className="px-3 py-1 bg-white/10 rounded-lg text-[8px] font-black uppercase border border-white/5">{tag}</span>
                             ))}
                           </div>
                        </div>
                      </div>
                   </div>
                </section>
                
                {/* Resume Access Button */}
                {viewingStudentDetail.resumeUrl && (
                  <a 
                    href={viewingStudentDetail.resumeUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-6 bg-slate-50 dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors group"
                  >
                     <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white dark:bg-slate-900 rounded-2xl flex items-center justify-center text-red-500 shadow-md">
                           <i className="fas fa-file-pdf"></i>
                        </div>
                        <div>
                           <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Access Credentials</h4>
                           <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">View Student Resume / Portfolio</p>
                        </div>
                     </div>
                     <div className="w-10 h-10 bg-white dark:bg-slate-900 rounded-full flex items-center justify-center text-slate-400 group-hover:text-blue-600 transition-colors shadow-sm">
                        <i className="fas fa-external-link-alt"></i>
                     </div>
                  </a>
                )}

                <div className="mt-auto pt-8 border-t border-slate-100 dark:border-slate-800 flex flex-col md:flex-row gap-4 relative z-10 pb-8 md:pb-0">
                   <button onClick={() => setViewingStudentDetail(null)} className="md:flex-1 py-5 text-slate-400 font-black uppercase text-xs tracking-widest hover:text-slate-900">Dismiss</button>
                   <button onClick={() => { handleAccept(requests.find(r => r.studentId === viewingStudentDetail.userId && r.status === RequestStatus.PENDING)?.id!); setViewingStudentDetail(null); }} className="md:flex-[2] py-6 bg-slate-900 dark:bg-indigo-600 text-white rounded-[1.8rem] font-black text-xl tracking-tighter shadow-2xl active:scale-95 hover:bg-slate-800">Initiate Connection Cycle</button>
                </div>
             </div>
           </div>
        )}

        {rejectingRequest && (
          <div className="fixed inset-0 z-[1000] bg-slate-900/50 dark:bg-black/95 backdrop-blur-xl flex items-center justify-center p-6 animate-in fade-in">
             <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[3rem] p-10 shadow-4xl animate-in zoom-in-95 border border-slate-200 dark:border-slate-800">
                <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tighter mb-2">Decline Sync?</h3>
                <p className="text-xs text-slate-500 font-medium mb-8">Inform the candidate why the hub is currently offline for this request.</p>
                <div className="space-y-3 mb-8">
                   {["Capacity reached.", "Sector misalignment.", "Technical scope too broad."].map((reason, i) => (
                     <button key={i} onClick={() => handleDecline(rejectingRequest.id, reason)} className="w-full p-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-900 dark:hover:bg-blue-950 hover:text-white rounded-xl text-[9px] font-black uppercase tracking-widest text-left transition-all border border-slate-100 dark:border-slate-700">{reason}</button>
                   ))}
                </div>
                <button onClick={() => setRejectingRequest(null)} className="w-full py-2 text-slate-400 font-black uppercase text-[9px] tracking-widest hover:text-slate-600">Cancel</button>
             </div>
          </div>
        )}
    </div>
  );
};

export default ProfessionalDashboard;
